import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, doc, getDocs, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { notify } from '../utils/notify';
import { mergeValues, parse } from './mergeRules';

/*
 * Device storage with an optional profile behind it.
 *
 * Everything the reader makes on the device (bookmarks, recently opened, progress, test
 * attempts, rotation notes, contact favourites, pinned documents, the TEE case log) goes
 * through readJSON/writeJSON here. Signed out, that is plain localStorage. Signed in, each
 * synced key is also a document at users/{uid}/data/{key} holding the JSON as a string:
 *
 *   - sign-in merges what is on the device with what the profile already holds, so a
 *     phone's history joins the profile the first time and nothing is lost either way;
 *   - every local write uploads (debounced); every remote change lands in localStorage
 *     and is announced with a 'devicestore:change' event so hooks and module stores reload;
 *   - sign-out removes the synced keys from the device. They stay in the profile.
 *
 * Preferences (dark mode, segment positions, disclaimer, install prompt) are not synced.
 */

export const STORE_EVENT = 'devicestore:change';
const SYNCED_EXACT = new Set(['education-saved', 'education-recent', 'education-progress', 'education-attempts', 'tee-case-log', 'notifications-read', 'notifications-dismissed']);
const SYNCED_PATTERNS = [/^notes:/, /-favorites$/, /-pinned-docs$/];
export const isSyncedKey = (key) => SYNCED_EXACT.has(key) || SYNCED_PATTERNS.some((re) => re.test(key));
const MAX_DOC_CHARS = 900000; // Firestore documents top out at 1 MiB

// ---- local -----------------------------------------------------------------------------------

export const readJSON = (key, fallback = null) => {
  try { const raw = localStorage.getItem(key); return raw === null ? fallback : JSON.parse(raw); } catch { return fallback; }
};
const rawOf = (key) => { try { return localStorage.getItem(key); } catch { return null; } };
const announce = (key) => { try { window.dispatchEvent(new CustomEvent(STORE_EVENT, { detail: { key } })); } catch { /* ignore */ } };

/** Write a value for this device (and the profile, when signed in). Throws if storage is full. */
export const writeJSON = (key, value) => {
  const text = JSON.stringify(value);
  localStorage.setItem(key, text);
  if (isSyncedKey(key)) queueUpload(key, text);
};

/** Subscribe to remote (or cross-store) changes of one key; returns unsubscribe */
export const subscribeKey = (key, cb) => {
  const handler = (e) => { if (!e.detail || e.detail.key === key) cb(); };
  window.addEventListener(STORE_EVENT, handler);
  return () => window.removeEventListener(STORE_EVENT, handler);
};

/** React: a stored value that follows remote changes. Read-only; write with writeJSON. */
export const useStoredValue = (key, fallback = null) => {
  const [value, setValue] = useState(() => readJSON(key, fallback));
  useEffect(() => { setValue(readJSON(key, fallback)); return subscribeKey(key, () => setValue(readJSON(key, fallback))); }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return value;
};

// ---- sync ------------------------------------------------------------------------------------

let uid = null;
let fresh = false; // set by the sign-in flow so the first merge can say what happened
const timers = new Map();
const applied = new Map(); // key -> last text we wrote locally from remote, to skip echoes

/** The sign-in sheet calls this right before Auth reports the new user */
export const markFreshSignIn = () => { fresh = true; };

const dataRef = (key) => doc(db, 'users', uid, 'data', key);

const upload = async (key, text) => {
  if (!uid) return;
  if (text.length > MAX_DOC_CHARS) { console.warn(`[deviceStore] ${key} too large to sync (${text.length} chars)`); return; }
  try { await setDoc(dataRef(key), { key, v: text, at: serverTimestamp() }); } catch (e) { console.warn('[deviceStore] upload failed', key, e.message); }
};
const queueUpload = (key, text) => {
  if (!uid) return;
  clearTimeout(timers.get(key));
  timers.set(key, setTimeout(() => { timers.delete(key); upload(key, text); }, 800));
};

const localSyncedKeys = () => {
  const keys = [];
  try { for (let i = 0; i < localStorage.length; i += 1) { const k = localStorage.key(i); if (isSyncedKey(k)) keys.push(k); } } catch { /* ignore */ }
  return keys;
};

const applyRemote = (key, text) => {
  if (rawOf(key) === text) return;
  try { localStorage.setItem(key, text); } catch { return; }
  applied.set(key, text);
  announce(key);
};

const initialMerge = async () => {
  const snap = await getDocs(collection(db, 'users', uid, 'data'));
  const remote = new Map(); snap.forEach((d) => { const v = d.data(); if (typeof v.v === 'string') remote.set(d.id, v.v); });
  const keys = new Set([...localSyncedKeys(), ...remote.keys()]);
  let pulled = 0; let pushed = 0;
  keys.forEach((key) => {
    const localText = rawOf(key);
    const remoteText = remote.has(key) ? remote.get(key) : null;
    const merged = mergeValues(key, parse(localText), parse(remoteText));
    if (merged === undefined) return;
    const text = JSON.stringify(merged);
    if (text !== localText) { applyRemote(key, text); pulled += 1; }
    if (text !== remoteText) { upload(key, text); pushed += 1; }
  });
  if (fresh) {
    fresh = false;
    if (pushed || pulled) notify.success('Saved items, progress, notes and the TEE log now sync with your profile.');
  }
};

const clearDevice = () => {
  localSyncedKeys().forEach((key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } announce(key); });
  applied.clear();
};

/** Call once at the app root. Follows Auth: merge and listen when signed in, wipe synced keys on sign-out. */
export const startDeviceSync = () => {
  let stopSnapshot = null;
  return onAuthStateChanged(auth, async (user) => {
    if (stopSnapshot) { stopSnapshot(); stopSnapshot = null; }
    const was = uid;
    if (!user || !user.email || !/@bcm\.edu$/i.test(user.email)) {
      uid = null;
      if (was) clearDevice();
      return;
    }
    uid = user.uid;
    try { await initialMerge(); } catch (e) { console.warn('[deviceStore] initial merge failed', e.message); }
    if (uid !== user.uid) return; // signed out while merging
    stopSnapshot = onSnapshot(collection(db, 'users', uid, 'data'), (snap) => {
      if (snap.metadata.hasPendingWrites) return;
      snap.docChanges().forEach((ch) => {
        if (ch.type === 'removed') return;
        const v = ch.doc.data();
        if (typeof v.v === 'string' && applied.get(ch.doc.id) !== v.v) applyRemote(ch.doc.id, v.v);
      });
    }, (e) => console.warn('[deviceStore] listen failed', e.message));
  });
};
